import React, { useContext } from 'react';
import {ApiContext} from "../context/ApiContext.jsx";
import {useAlert} from "./useAlert.jsx";
// import { useSnackbar } from '../context/SnackbarContext';

/**
 * Custom React hook to access API methods and a unified `callApi` function.
 * The `callApi` function handles loading state, error state,
 * toast notifications for success/error, and optionally bubbles errors.
 *
 */
export const useApi = () => {
    const apiContext = useContext(ApiContext);
    const {alertSuccess, alertError} = useAlert();
    if (!apiContext) {
        throw new Error(
            `useApi must be used within an ApiProvider`,
        )
    }

    const { api, setLoading, setError } = apiContext;
    // const toast = useSnackbar();

    /**
     * Executes an API request with centralized loading, error handling, and toast notifications.
     *
     * @async
     * @function callApi
     * @param {Object} options - Configuration options for the API call.
     * @param {() => Promise<*>} options.request - Async function that performs the API request.
     * @param {string | undefined} [options.successToast] - Optional success message for toast.
     * @param {string | undefined} [options.errorToast] - Optional error message for toast.
     * @param {boolean} [options.bubbleError] - Whether to re-throw errors after handling.
     *
     * @returns {Promise<*>} The result returned by the API request function.
     *
     * @throws {Error} Throws the original error if `bubbleError` is true.
     *
     * @example
     * const users = await callApi({
     *   request: async () => {
     *      return await api.getUsers();
     *   },
     *   successToast: "Users loaded successfully",
     *   errorToast: "Failed to load users",
     * });
     */
    const callApi = async ({request, successMessage, errorMessage, bubbleError = false, showError = true}) => {
        setLoading(true);
        setError(null);

        try {
            const result = await request();
            if (successMessage) alertSuccess(successMessage);
            return result;
        } catch (err) {
            setError(err);
            if(showError) {
                errorMessage ? alertError(errorMessage) : alertError(err.message)
            }
            if(bubbleError) throw err;
        } finally {
            setLoading(false);
        }
    };

    return { api, callApi };
};
